/**
 * @lines98/shared/util/date-of-day — derives `date_of_day` calendar string from а UTC timestamp.
 *
 * **Source of truth.** Daily-board rotation is anchored to а fixed UTC hour
 * boundary (`DAILY_ROLLOVER_BOUNDARY_UTC_HOURS = 21`, owner-decision 2026-05-23).
 * Two players who open the board at the same wall-clock moment must see the same
 * `daily_seed` — and `daily_seed = derive(date_of_day)` (ADR-0002). So the whole
 * fairness chain rests on this one pure function.
 *
 * **Algorithm (ADR-0002 §«Decision (date_of_day)»).** Shift the UTC timestamp
 * back by `(24h − boundary)` hours, then take the ISO-8601 calendar date of the
 * shifted instant. With `boundary = 21`:
 *   - `2026-05-23T20:59:59Z` → shift +3h → `2026-05-23T23:59:59Z` → `"2026-05-23"`.
 *   - `2026-05-23T21:00:00Z` → shift +3h → `2026-05-24T00:00:00Z` → `"2026-05-24"`.
 *
 * The function lives in `@lines98/shared` (not `@lines98/server`) because both
 * client and server compute it — client for countdown / localized day label
 * (`core-daily-board.md` §2.4), server for authoritative `daily_seed` derivation
 * (ADR-0002 §«Серверный flow»). Having one implementation guarantees no drift.
 *
 * **Native Date only.** Per ADR-0002 §«Options Considered (date_of_day)»: no
 * `Temporal`, no `dayjs`/`luxon`/`date-fns` — а 3-line shift in UTC suffices and
 * keeps the client bundle small. DST is irrelevant in UTC; leap-second handling
 * is intentionally JS-native (Date treats UTC as continuous).
 */
import { DAILY_ROLLOVER_BOUNDARY_UTC_HOURS } from '../config/balance.js';
/** Milliseconds per hour — used to shift the UTC instant. */
const MS_PER_HOUR = 3_600_000;
/**
 * Derive the calendar `date_of_day` (YYYY-MM-DD) для а UTC instant.
 *
 * Accepts either а `Date` или а numeric epoch-milliseconds value; both produce
 * identical output, which lets callers pass either `new Date()` или а server-side
 * `Date.now()` без conversion ceremony.
 *
 * @param utcTimestamp — wall-clock instant в UTC (Date object or epoch ms).
 * @returns `"YYYY-MM-DD"` — the day that the daily board belongs to at this instant.
 *
 * @example
 *   dateOfDayFromUtc(new Date('2026-05-23T20:59:59Z'));  // → '2026-05-23' (pre-rollover)
 *   dateOfDayFromUtc(new Date('2026-05-23T21:00:00Z'));  // → '2026-05-24' (post-rollover)
 *   dateOfDayFromUtc(Date.UTC(2026, 4, 24));             // → number input is also fine
 */
export function dateOfDayFromUtc(utcTimestamp) {
    const epochMs = typeof utcTimestamp === 'number' ? utcTimestamp : utcTimestamp.getTime();
    const shiftHours = 24 - DAILY_ROLLOVER_BOUNDARY_UTC_HOURS;
    const shiftedMs = epochMs + shiftHours * MS_PER_HOUR;
    // toISOString() is locale-independent and always uses UTC.
    return new Date(shiftedMs).toISOString().slice(0, 10);
}
/** Milliseconds per day — span between consecutive rollover boundaries. */
const MS_PER_DAY = 24 * MS_PER_HOUR;
/**
 * Compute the epoch-ms of the **next** daily-rollover boundary strictly after
 * `nowMs`. The boundary is `DAILY_ROLLOVER_BOUNDARY_UTC_HOURS:00 UTC` (21:00)
 * each calendar day.
 *
 * Used by the replay teaser endpoint (T-9, anti-cheat.md §3A.3 / AC-17) to
 * compute `countdown_to_rollover_seconds` — the moment a today's-daily teaser
 * turns playable for everyone (§7.15). One source of truth so the countdown
 * and `dateOfDayFromUtc` can never disagree about where the day boundary sits.
 *
 * **Strictly after:** at exactly `21:00:00 UTC` the boundary has just been
 * crossed (the board already rolled over per `dateOfDayFromUtc`), so the next
 * boundary is the following day's 21:00 — countdown restarts at a full window
 * rather than reporting 0. This keeps the countdown monotonically positive.
 *
 * @param nowMs — current server instant (epoch ms).
 * @returns epoch-ms of the next 21:00-UTC boundary strictly greater than `nowMs`.
 *
 * @example
 *   nextRolloverUtcMs(Date.UTC(2026, 4, 24, 12, 0, 0)); // → 2026-05-24T21:00:00Z
 *   nextRolloverUtcMs(Date.UTC(2026, 4, 24, 21, 0, 0)); // → 2026-05-25T21:00:00Z
 */
export function nextRolloverUtcMs(nowMs) {
    const now = new Date(nowMs);
    // Today's boundary at 21:00 UTC (using the calendar date of `nowMs` in UTC).
    const todaysBoundary = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), DAILY_ROLLOVER_BOUNDARY_UTC_HOURS, 0, 0, 0);
    // Strictly after: if we're at/past today's boundary, jump to tomorrow's.
    return nowMs >= todaysBoundary ? todaysBoundary + MS_PER_DAY : todaysBoundary;
}
//# sourceMappingURL=date-of-day.js.map